/* eslint-disable no-bitwise */

'use client';

import type { ReactNode, RefObject } from 'react';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';
import { noop } from 'utils/noop';
import { useDeepMemo } from '@react-aria/utils';

type DescendantElement<T extends HTMLElement = HTMLElement> = RefObject<T>;

export type Descendant<
  T extends HTMLElement = HTMLElement,
  O extends Record<string, any> = Record<string, any>
> = {
  id: string;
  element: DescendantElement<T>;
} & O;

export type DescendantsContextType<
  T extends HTMLElement = HTMLElement,
  O extends Record<string, any> = Record<string, any>
> = {
  descendants?: Array<Descendant<T, O>>;
  registerDescendant: (
    id: string,
    element: DescendantElement<T>,
    options: O
  ) => void;
  unregisterDescendant: (element: DescendantElement<T>) => void;
};

export type DescendantsProviderProps<
  T extends HTMLElement = HTMLElement,
  O extends Record<string, any> = Record<string, any>
> = {
  children: ReactNode;
  descendants?: Array<Descendant<T, O>>;
  setDescendants: React.Dispatch<React.SetStateAction<Descendant<T, O>[]>>;
};

/**
 * Context used to track and manage hierarchical descendants (children) in component trees.
 * Provides registration, ordering, and traversal capabilities for components like
 * MenuList and other composite components that need to manage focus
 * and selection across their children.
 */

export function createDescendantsContext<
  T extends HTMLElement = HTMLElement,
  O extends Record<string, any> = Record<string, any>
>() {
  const defaultContext: DescendantsContextType<T, O> = {
    descendants: [],
    registerDescendant: noop,
    unregisterDescendant: noop,
  };

  const DescendantsContext = React.createContext(defaultContext);

  /**
   * Hook for descendant components to register themselves with their parent context.
   * @param {string} id - Unique identifier for the descendant
   * @param {DescendantElement<T>} elementRef - Reference to the DOM element
   * @param {O} options - Additional configuration options for the descendant
   * @returns Object containing registration info and the descendant's index
   */

  const useDescendant = (
    id: string,
    elementRef: DescendantElement<T>,
    options: O = {} as O
  ) => {
    const { registerDescendant, unregisterDescendant, descendants } =
      useContext(DescendantsContext);

    const register = useRef(registerDescendant);
    const unregister = useRef(unregisterDescendant);

    useIsomorphicLayoutEffect(() => {
      register.current = registerDescendant;
      unregister.current = unregisterDescendant;
    }, [registerDescendant, unregisterDescendant]);

    const memoizedOptions = useDeepMemo<O>(options, (prev, next) => {
      return Object.keys(prev).every((key) => prev[key] === next[key]);
    });

    useIsomorphicLayoutEffect(() => {
      register.current(id, elementRef, memoizedOptions);
      return () => unregister.current(elementRef);
    }, [elementRef, id, memoizedOptions]);

    const currentIndex = useMemo(
      () => descendants?.findIndex((item) => item.element === elementRef),
      [descendants, elementRef]
    );

    return { registerDescendant, index: currentIndex };
  };

  /**
   * Provider component that manages descendant registration and ordering.
   * Maintains a sorted list of descendants based on DOM order and provides
   * context for child components to register themselves.
   * Used in composite components like MenuList to track focusable children.
   */

  const DescendantsProvider = ({
    children,
    descendants: items,
    setDescendants: setItems,
  }: DescendantsProviderProps<T, O>) => {
    const registerDescendant = useCallback(
      (id: string, el: DescendantElement<T>, options: O) => {
        setItems((existingItems) => {
          const registered = existingItems.some(
            ({ element }) => element === el
          );

          if (!registered) {
            const newItem = { id, element: el, ...options };
            const newItems = [...existingItems, newItem];
            return newItems.sort((a, b) => {
              if (!a.element.current || !b.element.current) {
                return 0;
              }
              return sortByDocumentPosition(
                a.element.current,
                b.element.current
              );
            });
          }

          return existingItems;
        });
      },
      [setItems]
    );

    const stableSetItems = useRef(setItems);

    useIsomorphicLayoutEffect(() => {
      stableSetItems.current = setItems;
    }, [setItems]);

    const unregisterDescendant = useCallback((el: DescendantElement<T>) => {
      if (!el) {
        return;
      }

      stableSetItems.current((existingItems) =>
        existingItems.filter(({ element }) => element !== el)
      );
    }, []);

    const context = useMemo(() => {
      return {
        descendants: items,
        registerDescendant,
        unregisterDescendant,
      };
    }, [items, registerDescendant, unregisterDescendant]);

    return (
      <DescendantsContext.Provider value={context}>
        {children}
      </DescendantsContext.Provider>
    );
  };

  return {
    DescendantsContext,
    useDescendant,
    DescendantsProvider,
  };
}

/**
 * @deprecated These exports are deprecated and will be removed in a future version.
 * Instead, use createDescendantsContext() directly in your component to ensure
 * descendants are properly scoped to your particular usage.
 *
 * Example:
 * ```
 * const { DescendantsContext, useDescendant, DescendantsProvider } = createDescendantsContext();
 * ```
 *
 * This approach prevents cross-contamination between different components using descendants.
 */

const { DescendantsContext, useDescendant, DescendantsProvider } =
  createDescendantsContext();

export { DescendantsContext, DescendantsProvider, useDescendant };

/**
 * Sorts two nodes by their document position.
 * @param {Node} a
 * @param {Node} b
 * @returns {number}
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function sortByDocumentPosition(a: Node, b: Node): number {
  const position = a.compareDocumentPosition(b);

  if (
    position & Node.DOCUMENT_POSITION_FOLLOWING ||
    position & Node.DOCUMENT_POSITION_CONTAINED_BY
  ) {
    return -1;
  }

  if (
    position & Node.DOCUMENT_POSITION_PRECEDING ||
    position & Node.DOCUMENT_POSITION_CONTAINS
  ) {
    return 1;
  }

  return 0;
}
